import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Pin, Star, ArrowRight, LogIn } from "lucide-react";

const LIMIT = 20;

export default function MyBoardSidebar({ version = 0 }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);

  useEffect(() => {
    if (user?.role !== "client") return;
    api.get("/boards/me").then(r => setBoard(r.data)).catch(() => {});
  }, [user?.id, user?.role, version]);

  const Shell = ({ children }) => (
    <aside className="lg:w-80 shrink-0" data-testid="my-board-sidebar">
      <div className="lg:sticky lg:top-6 bg-white rounded-2xl border border-black/5 shadow-sm p-5">
        <h2 className="text-lg font-bold flex items-center gap-2 mb-1" style={{fontFamily:'Outfit'}}>
          <Pin size={18} className="text-[#C79B6B]"/> My Board
        </h2>
        {children}
      </div>
    </aside>
  );

  // Visitor — prompt to sign in
  if (user === false) {
    return (
      <Shell>
        <p className="text-sm text-[#222]/60 mb-4">Save the pros you love and build your personal neighborhood board.</p>
        <button onClick={() => navigate("/login")} className="btn-lime w-full py-2 text-sm flex items-center justify-center gap-2" data-testid="sidebar-signin-btn">
          <LogIn size={14}/> Sign in to start
        </button>
      </Shell>
    );
  }

  // Pros / admins don't have a board
  if (user && user.role !== "client") {
    return (
      <Shell>
        <p className="text-sm text-[#222]/60">My Board is available on client accounts to save and organize your favorite local pros.</p>
      </Shell>
    );
  }

  // Loading auth/board
  if (user === null || (user?.role === "client" && board === null)) {
    return (
      <Shell>
        <div className="animate-pulse space-y-3 mt-3">
          {[0,1,2].map(i => <div key={i} className="h-12 bg-black/5 rounded-xl"/>)}
        </div>
      </Shell>
    );
  }

  const pins = board?.pins?.filter(p => !p.inactive) || [];
  const preview = pins.slice(0, 6);

  return (
    <Shell>
      <p className="text-xs text-[#222]/50 mb-4" data-testid="sidebar-count">{board?.count ?? 0} / {LIMIT} pinned</p>

      {preview.length === 0 ? (
        <p className="text-sm text-[#222]/60 mb-4">Pin pros from the directory to start your board.</p>
      ) : (
        <div className="space-y-2 mb-4">
          {preview.map(p => (
            <button
              key={p.user_id}
              onClick={() => navigate(`/pro/${p.user_id}`)}
              className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-black/[0.03] transition text-left"
              data-testid={`sidebar-pin-${p.user_id}`}
            >
              <div className="w-9 h-9 rounded-full overflow-hidden bg-[#F2F4F2] shrink-0 grid place-items-center">
                {p.avatar_url
                  ? <img src={p.avatar_url} alt="" className="w-full h-full object-cover"/>
                  : <span className="font-bold text-sm text-[#222]/40">{p.business_name?.[0] || "?"}</span>}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{p.business_name}</p>
                <p className="text-[11px] text-[#222]/50 uppercase tracking-wider truncate">{p.category}</p>
              </div>
              {p.rating > 0 && (
                <span className="flex items-center gap-0.5 text-xs text-[#222]/60 shrink-0">
                  <Star size={11} className="fill-[#F59E0B] text-[#F59E0B]"/> {p.rating.toFixed(1)}
                </span>
              )}
            </button>
          ))}
          {pins.length > preview.length && (
            <p className="text-xs text-[#222]/45 px-2">+ {pins.length - preview.length} more</p>
          )}
        </div>
      )}

      <button onClick={() => navigate("/board")} className="w-full py-2 rounded-full border border-black/10 text-sm font-semibold hover:bg-black/5 transition flex items-center justify-center gap-1" data-testid="sidebar-view-board-btn">
        View My Board <ArrowRight size={14}/>
      </button>
    </Shell>
  );
}
